import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.category === activeSlug);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).reverse();
    }
    return result;
  }, [activeSlug, sortBy]);

  const pageTitle = activeCategory ? `${activeCategory.name}漫画 - 韩漫漫画网` : '漫画分类 - 韩漫漫画网';
  const pageDesc = activeCategory
    ? `韩漫漫画网${activeCategory.name}漫画分类，海量${activeCategory.name}类韩漫免费在线观看，每日更新，高清画质流畅阅读。`
    : '韩漫漫画网漫画分类，热血、恋爱、奇幻、悬疑等各类韩漫一网打尽，漫画完整在线观看尽在韩漫漫画网。';

  const catSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://www.hanmancomic.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫漫画网',
      url: 'https://www.hanmancomic.com/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(catSchema) }}
      />

      <div className="w-full px-4 md:px-6 py-8 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 font-bold mb-6">
            {activeCategory ? `${activeCategory.name}漫画` : '漫画分类'}
          </h1>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 flex-wrap mb-6 bg-background-100 p-1.5 rounded-full w-fit">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>

          {/* Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-600">
              共找到 <strong className="text-primary-500">{filteredComics.length}</strong> 部漫画
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key as typeof sortBy)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${activeCategory?.name || ''}韩漫免费在线阅读`}
                    title={`${comic.title} - 韩漫漫画网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full font-medium">
                    {comic.rating}
                  </div>
                  {comic.status === '已完结' && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-background-900/70 text-white text-xs rounded-full">
                      已完结
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-foreground-500">{comic.author}</p>
                  <span className="text-xs text-foreground-400">|</span>
                  <p className="text-xs text-foreground-500">
                    更新至{comic.chapters.length}话
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-400 mb-4 block"></i>
              <p className="text-foreground-600">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}